import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Dialog } from "@mui/material";
import * as React from "react";

import { PageLayout } from "@/shared/components";
import { AddToCollection } from "@/shared/components/collections";
import type {
  FilterDefinition,
  SelectionOption,
} from "@/shared/components/ResourceView";
import {
  coreSessionFilters,
  CreateSessionButton,
  sessionResourceIdFilter,
  SessionsList,
} from "@/shared/components/sessions";
import type { SessionRecord } from "@/shared/domain/sessions";
import { usePopupState } from "@/shared/hooks/usePopupState";

/**
 * Filter-menu surface for the unscoped `/sessions` page. Both
 * cross-aggregate id rows are user-narrowable here because no server-side
 * scope is applied.
 */
const sessionsPageFilterDefinitions: FilterDefinition[] = [
  ...coreSessionFilters,
  {
    type: "string",
    field: "dataset.dataset_id",
    name: "Dataset ID",
    allowedComparators: sessionResourceIdFilter.comparators,
    icon: sessionResourceIdFilter.icon,
  },
  {
    type: "string",
    field: "device.device_id",
    name: "Device ID",
    allowedComparators: sessionResourceIdFilter.comparators,
    icon: sessionResourceIdFilter.icon,
  },
];

/**
 * Top-level `/sessions` page. Renders the shared `SessionsList` with the
 * unscoped filter picker, a toolbar entry point into the create-session
 * flow, and a bulk "Add to Collection" selection action.
 */
export function SessionsPage() {
  const addToCollectionPopup = usePopupState<Set<string>>();
  const { open: openAddToCollectionPopup } = addToCollectionPopup;

  const selectionOptions: SelectionOption<SessionRecord>[] = React.useMemo(
    () => [
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected }) => {
          openAddToCollectionPopup({
            metadata: new Set(selected.map((s) => s.session_id)),
          });
        },
      },
    ],
    [openAddToCollectionPopup],
  );

  return (
    <PageLayout title="Roboto - Sessions" headerContentLeft="Sessions">
      <SessionsList
        filterDefinitions={sessionsPageFilterDefinitions}
        noRowsContent="No sessions created yet"
        actions={<CreateSessionButton />}
        selectionOptions={selectionOptions}
      />
      <Dialog
        open={addToCollectionPopup.isOpen}
        onClose={addToCollectionPopup.close}
      >
        <AddToCollection
          selected={{
            type: "Session",
            items: addToCollectionPopup.metadata ?? new Set(),
          }}
          onCancel={addToCollectionPopup.close}
        />
      </Dialog>
    </PageLayout>
  );
}
